/**
 * Documents Tab Component for Client Detail Page
 * Displays clinical documents with view/print/delete options
 */
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Upload, Trash2, File, FolderOpen, Eye, Printer, Download, ShieldCheck, X } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { usePermissions } from "@/context/PermissionsContext";

const API = process.env.REACT_APP_BACKEND_URL;

const DOCUMENT_TYPES = [
  { value: "cms_485", label: "CMS 485 Form" },
  { value: "physician_order", label: "Physician Order" },
  { value: "assessment", label: "Assessment" },
  { value: "admission", label: "Admission Form" },
  { value: "care_plan", label: "Care Plan" },
  { value: "progress_note", label: "Progress Note" },
  { value: "insurance", label: "Insurance Document" },
  { value: "consent", label: "Consent Form" },
  { value: "lab_result", label: "Lab Result" },
  { value: "signed_roi", label: "Signed ROI" },
  { value: "other", label: "Other" },
];

export function DocumentsTab({
  documents,
  onUploadClick,
  onDelete,
  categoryFilter = "all",
  onCategoryFilterChange,
}) {
  const { hasPermission } = usePermissions();
  const canDownload = hasPermission("documents", "download");
  const canUpload = hasPermission("documents", "upload");
  const canDeleteDoc = hasPermission("documents", "delete");
  // Get full URL for document
  const getDocUrl = (doc) => {
    if (!doc.file_url) return null;
    // If it's already a full URL, return as-is
    if (doc.file_url.startsWith('http')) return doc.file_url;
    // Otherwise, prepend the API base URL
    return `${API}${doc.file_url}`;
  };

  const handleView = async (doc) => {
    const url = getDocUrl(doc);
    if (!url) {
      toast.error("Document URL not available");
      return;
    }
    
    try {
      // Fetch with auth token
      const response = await axios.get(url, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: doc.content_type || 'application/octet-stream' });
      const blobUrl = URL.createObjectURL(blob);
      window.open(blobUrl, '_blank');
    } catch (error) {
      console.error("Failed to view document:", error);
      toast.error("Failed to open document");
    }
  };

  const handlePrint = async (doc) => {
    const url = getDocUrl(doc);
    if (!url) {
      toast.error("Document URL not available");
      return;
    }

    const fileExtension = doc.filename?.split('.').pop()?.toLowerCase();
    
    try {
      // Fetch the file with auth
      const response = await axios.get(url, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: doc.content_type || 'application/octet-stream' });
      const blobUrl = URL.createObjectURL(blob);
      
      if (fileExtension === 'pdf') {
        // Open PDF in new window and trigger print
        const printWindow = window.open(blobUrl, '_blank');
        if (printWindow) {
          printWindow.onload = () => {
            setTimeout(() => {
              printWindow.print();
            }, 500);
          };
        }
      } else if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(fileExtension)) {
        // For images, create a printable page
        const printWindow = window.open('', '_blank');
        if (printWindow) {
          printWindow.document.write(`
            <!DOCTYPE html>
            <html>
            <head>
              <title>Print - ${doc.filename}</title>
              <style>
                body { margin: 0; padding: 20px; display: flex; justify-content: center; align-items: center; min-height: 100vh; }
                img { max-width: 100%; max-height: 100vh; object-fit: contain; }
                @media print {
                  body { padding: 0; }
                  img { max-width: 100%; max-height: 100%; }
                }
              </style>
            </head>
            <body>
              <img src="${blobUrl}" alt="${doc.filename}" onload="setTimeout(() => window.print(), 300)" />
            </body>
            </html>
          `);
          printWindow.document.close();
        }
      } else {
        // For other file types, download instead
        toast.info("This file type cannot be printed directly. Downloading instead...");
        handleDownload(doc);
      }
    } catch (error) {
      console.error("Failed to print document:", error);
      toast.error("Failed to print document");
    }
  };

  const handleDownload = async (doc) => {
    const url = getDocUrl(doc);
    if (!url) {
      toast.error("Document URL not available");
      return;
    }
    
    try {
      const response = await axios.get(url, { responseType: 'blob' });
      const blob = new Blob([response.data]);
      const blobUrl = URL.createObjectURL(blob);
      
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = doc.filename || 'document';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
      
      toast.success("Document downloaded!");
    } catch (error) {
      console.error("Failed to download document:", error);
      toast.error("Failed to download document");
    }
  };

  return (
    <div data-testid="client-documents-tab">
      <div className="form-section">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <h3 className="text-lg font-semibold text-slate-800">
            Clinical Documents ({(categoryFilter && categoryFilter !== "all"
              ? documents.filter((d) => d.category === categoryFilter || d.document_type === categoryFilter)
              : documents).length})
          </h3>
          <div className="flex items-center gap-2">
            {categoryFilter && categoryFilter !== "all" && (
              <button
                type="button"
                onClick={() => onCategoryFilterChange && onCategoryFilterChange("all")}
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium bg-emerald-50 border border-emerald-200 text-emerald-700 hover:bg-emerald-100"
                data-testid="docs-clear-filter-btn"
              >
                <ShieldCheck className="w-3.5 h-3.5" />
                Filter: {DOCUMENT_TYPES.find((t) => t.value === categoryFilter)?.label || categoryFilter}
                <X className="w-3.5 h-3.5" />
              </button>
            )}
            {canUpload && (
            <Button
              size="sm"
              onClick={onUploadClick}
              className="gap-2"
              data-testid="upload-document-btn"
            >
              <Upload className="w-4 h-4" />
              Upload Document
            </Button>
            )}
          </div>
        </div>

        {(() => {
          const visible =
            categoryFilter && categoryFilter !== "all"
              ? documents.filter(
                  (d) => d.category === categoryFilter || d.document_type === categoryFilter
                )
              : documents;
          return visible.length === 0 ? (
          <div className="text-center py-8 text-slate-500 bg-slate-50 rounded-lg">
            <FolderOpen className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p className="text-sm">
              {categoryFilter && categoryFilter !== "all"
                ? "No documents match this filter"
                : "No documents uploaded yet"}
            </p>
            <p className="text-xs mt-1">Upload clinical documents for this client</p>
          </div>
        ) : (
          <div className="space-y-3">
            {visible.map((doc) => (
              <div
                key={doc.id}
                className="flex items-center justify-between p-4 border border-slate-200 rounded-lg hover:bg-slate-50"
                data-testid={`document-${doc.id}`}
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                    <File className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <p className="font-medium text-slate-800">{doc.filename}</p>
                    <div className="flex items-center gap-2 text-xs text-slate-500">
                      <span className="px-2 py-0.5 rounded bg-slate-100">
                        {DOCUMENT_TYPES.find(t => t.value === doc.document_type)?.label || doc.document_type || doc.category}
                      </span>
                      <span>•</span>
                      <span>{doc.uploaded_at?.split('T')[0] || doc.created_at?.split('T')[0]}</span>
                      {(doc.file_size || doc.size) && (
                        <>
                          <span>•</span>
                          <span>{((doc.file_size || doc.size) / 1024).toFixed(1)} KB</span>
                        </>
                      )}
                    </div>
                    {doc.notes && (
                      <p className="text-xs text-slate-400 mt-1">{doc.notes}</p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  {!canDownload && (
                    <span className="text-xs text-slate-400 italic px-2" data-testid={`doc-locked-${doc.id}`}>
                      View restricted
                    </span>
                  )}
                  {canDownload && (
                  <>
                  <button
                    onClick={() => handleView(doc)}
                    className="p-2 hover:bg-slate-100 rounded-lg"
                    title="View"
                    data-testid={`view-doc-${doc.id}`}
                  >
                    <Eye className="w-4 h-4 text-slate-600" />
                  </button>
                  <button
                    onClick={() => handlePrint(doc)}
                    className="p-2 hover:bg-blue-50 rounded-lg"
                    title="Print"
                    data-testid={`print-doc-${doc.id}`}
                  >
                    <Printer className="w-4 h-4 text-blue-600" />
                  </button>
                  <button
                    onClick={() => handleDownload(doc)}
                    className="p-2 hover:bg-emerald-50 rounded-lg"
                    title="Download"
                    data-testid={`download-doc-${doc.id}`}
                  >
                    <Download className="w-4 h-4 text-emerald-600" />
                  </button>
                  </>
                  )}
                  {canDeleteDoc && (
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <button 
                        className="p-2 hover:bg-red-50 rounded-lg text-red-600"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Delete Document?</AlertDialogTitle>
                        <AlertDialogDescription>
                          This will permanently delete &quot;{doc.filename}&quot;. This action cannot be undone.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                          className="bg-red-600 hover:bg-red-700"
                          onClick={() => onDelete(doc.id)}
                        >
                          Delete
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                  )}
                </div>
              </div>
            ))}
          </div>
        );
        })()}
      </div>
    </div>
  );
}
